import React, { Fragment, useCallback, useEffect, useState } from 'react'
import UserLayout from '../../layouts/UserLayout'
import {
	Backdrop,
	Box,
	Card,
	CircularProgress,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../../utils/error'
import { currentUserActions } from '../../redux/store'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { DOMAIN } from '../../utils/api'
import { getFromStorage } from '../../utils/storage'
import PickDateWithoutShiftGrow from '../../components/PickDateWithoutShiftGrow'
import { transformDetailWithPhone } from '../../utils/transformDetail'
import { mainGreen, mainRed } from '../../utils/color'
import { fetchStores, selectStores } from '../../redux/slices/storeSlice'

const STORELESS_ROLES = ['admin', 'director', 'assistant']

export default function TransferOrderStatistics() {
	const mediaMd = useMediaQuery('(min-width:768px)')
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { currentUser } = useSelector((state) => state.currentUser)
	const token = getFromStorage('current-user')?.accessToken
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const [statistics, setStatistics] = useState([])
	const [currentImage, setCurrentImage] = useState(null)
	const [isViewerOpen, setIsViewerOpen] = useState(false)
	const [currentIndex, setCurrentIndex] = useState()
	const [currentSelect, setCurrentSelect] = useState()

	// Admin/director/assistant have no store of their own: they pick which store to view
	const pickStore = STORELESS_ROLES.includes(currentUser.role)
	const stores = useSelector(selectStores)
	const [pickedStoreId, setPickedStoreId] = useState('')
	const viewedStoreId = pickStore ? pickedStoreId : currentUser.store
	const viewedStoreName = pickStore ? stores?.find((store) => store._id === pickedStoreId)?.name : currentUser.storeName

	useEffect(() => {
		if (pickStore && !stores?.length) dispatch(fetchStores())
	}, [pickStore, stores?.length, dispatch])

	useEffect(() => {
		if (pickStore && !pickedStoreId && stores?.length) setPickedStoreId(stores[0]._id)
	}, [pickStore, pickedStoreId, stores])

	const openImageViewer = (photo) => {
		setCurrentImage(photo)
		setIsViewerOpen(true)
	}

	const closeImageViewer = () => {
		setCurrentImage(null)
		setIsViewerOpen(false)
	}

	const today = new Date()

	const [firstTimePickGrow, setFirstTimePickGrow] = useState({
		date: today.getDate()?.toString(),
		month: (today.getMonth() + 1)?.toString(),
		year: today.getFullYear()?.toString(),
	})

	const [secondTimePickGrow, setSecondTimePickGrow] = useState({
		date: today.getDate()?.toString(),
		month: (today.getMonth() + 1)?.toString(),
		year: today.getFullYear()?.toString(),
	})

	const setFirstTimeGrow = (time) => {
		setFirstTimePickGrow(time)
	}

	const setSecondTimeGrow = (time) => {
		setSecondTimePickGrow(time)
	}

	const getStatistics = useCallback(
		async function () {
			if (!viewedStoreId) return
			setLoadingStatistics(true)
			try {
				const response = await axios({
					url: `${DOMAIN}/store-manager/statistics/transfer-order`,
					headers: {
						Authorization: `Bearer ${token}`,
						...(pickStore && { 'X-Acting-Store': viewedStoreId }),
					},
					method: 'POST',
					data: { firstTimePickGrow, secondTimePickGrow },
					withCredentials: true,
				})
				if (response.data.message) {
					alert(response.data.message)
					return
				}
				setStatistics(response.data.statistics)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, firstTimePickGrow, secondTimePickGrow, navigate, dispatch, pickStore, viewedStoreId],
	)

	useEffect(() => {
		getStatistics()
	}, [getStatistics])

	return (
		<UserLayout>
			<Backdrop
				sx={{
					color: '#fff',
					zIndex: 999,
					left: mediaMd ? 300 : 0,
				}}
				open={isViewerOpen}
				onClick={closeImageViewer}>
				<img
					loading='lazy'
					src={currentImage}
					alt='attach'
					style={{
						maxHeight: '70%',
						maxWidth: '90%',
					}}
					onClick={(e) => {
						e.stopPropagation()
					}}
				/>
			</Backdrop>
			<Box sx={{ background: '#fff', padding: '20px 0' }}>
				<Typography
					sx={{
						textAlign: 'center',
						fontSize: '18px',
						fontWeight: 'bold',
						letterSpacing: '2px',
						mb: 1,
					}}>
					THỐNG KÊ {viewedStoreName}
				</Typography>
				{pickStore && (
					<Box display='flex' justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
						<Typography sx={{ mr: 2 }}>Cửa hàng</Typography>
						<Select
							size='small'
							value={pickedStoreId}
							onChange={(e) => setPickedStoreId(e.target.value)}
							sx={{ minWidth: 180 }}>
							{stores?.map((store) => (
								<MenuItem key={store._id} value={store._id}>
									{store.name}
								</MenuItem>
							))}
						</Select>
					</Box>
				)}
				<Box display='flex' justifyContent='center' alignItems='flex-end'>
					<Typography sx={{ mr: 2, pb: 1 }}>Từ</Typography>
					<PickDateWithoutShiftGrow setTime={setFirstTimeGrow} time={firstTimePickGrow} />
				</Box>
				<Box display='flex' justifyContent='center' alignItems='flex-end'>
					<Typography sx={{ mr: 2, pb: 1 }}>đến</Typography>
					<PickDateWithoutShiftGrow setTime={setSecondTimeGrow} time={secondTimePickGrow} />
				</Box>
				{loadingStatistics && (
					<Box display='flex' justifyContent='center' sx={{ mt: 3, mb: 2 }}>
						<CircularProgress sx={{ color: mainGreen }} />
					</Box>
				)}
				{!loadingStatistics && (
					<Box
						sx={{
							margin: '0 auto',
							padding: '10px',
							display: 'block',
							overflow: 'auto',
						}}>
						<Table sx={{ minWidth: 500, background: '#fff' }}>
							<TableHead sx={{ background: mainGreen, border: 'none' }}>
								<TableRow>
									<TableCell
										sx={{
											padding: '10px 0',
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										Cửa hàng
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											padding: 0,
										}}>
										Tổng chuyển (kg)
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										Tổng nhận (kg)
									</TableCell>
									<TableCell
										rowSpan={2}
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										Chuyển - Nhận
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{statistics?.map((item, index) => {
									return (
										<TableRow key={index}>
											<TableCell
												sx={{
													padding: '10px 0',
													borderRight: '1px solid #000',
													fontWeight: 'bold',
												}}>
												{item?.store}
											</TableCell>
											<TableCell
												sx={{
													padding: '10px 0',
													borderRight: '1px solid #000',
													fontWeight: 'bold',
													cursor: 'pointer',
													'&:hover': {
														textDecoration: 'underline',
													},
												}}
												onClick={() => {
													setCurrentIndex(index)
													setCurrentSelect('transfer')
												}}>
												{(
													Number(item?.transferOrder) -
													item?.transferOrderDetail?.reduce(
														(sum, it) =>
															(sum +=
																it?.status === 'confirm-delete-produce' ||
																it?.status === 'confirm-delete-ship' ||
																it?.status === 'confirm-delete-success'
																	? +it?.quantity
																	: 0),
														0,
													)
												).toFixed(1)}
											</TableCell>
											<TableCell
												sx={{
													padding: '10px 0',
													borderRight: '1px solid #000',
													fontWeight: 'bold',
													cursor: 'pointer',
													'&:hover': {
														textDecoration: 'underline',
													},
												}}
												onClick={() => {
													setCurrentIndex(index)
													setCurrentSelect('receive')
												}}>
												{(
													Number(item?.receiveOrder) -
													item?.receiveOrderDetail?.reduce(
														(sum, it) =>
															(sum +=
																it?.status === 'confirm-delete-produce' ||
																it?.status === 'confirm-delete-ship' ||
																it?.status === 'confirm-delete-success'
																	? +it?.quantity
																	: 0),
														0,
													)
												).toFixed(1)}
											</TableCell>
											<TableCell
												sx={{
													padding: '10px 0',
													borderRight: '1px solid #000',
													fontWeight: 'bold',
												}}>
												{(
													Number(item?.transferOrder) -
													Number(item?.receiveOrder) +
													item?.transferOrderDetail?.reduce(
														(sum, it) =>
															(sum +=
																it?.status === 'confirm-delete-produce' ||
																it?.status === 'confirm-delete-ship' ||
																it?.status === 'confirm-delete-success'
																	? +it?.quantity
																	: 0),
														0,
													) +
													item?.receiveOrderDetail?.reduce(
														(sum, it) =>
															(sum +=
																it?.status === 'confirm-delete-produce' ||
																it?.status === 'confirm-delete-ship' ||
																it?.status === 'confirm-delete-success'
																	? +it?.quantity
																	: 0),
														0,
													)
												).toFixed(1)}
											</TableCell>
										</TableRow>
									)
								})}
								<TableRow sx={{ background: mainRed }}>
									<TableCell
										sx={{
											padding: '10px 0',
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										<b>TỔNG</b>
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										<b>
											{statistics
												?.reduce(
													(sum, item) =>
														(sum +=
															+item.transferOrder -
															item?.transferOrderDetail?.reduce(
																(sum, it) =>
																	(sum +=
																		it?.status === 'confirm-delete-produce' ||
																		it?.status === 'confirm-delete-ship' ||
																		it?.status === 'confirm-delete-success'
																			? +it?.quantity
																			: 0),
																0,
															)),
													0,
												)
												?.toFixed(1)}
										</b>
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										<b>
											{statistics
												?.reduce(
													(sum, item) =>
														(sum +=
															+item.receiveOrder -
															item?.receiveOrderDetail?.reduce(
																(sum, it) =>
																	(sum +=
																		it?.status === 'confirm-delete-produce' ||
																		it?.status === 'confirm-delete-ship' ||
																		it?.status === 'confirm-delete-success'
																			? +it?.quantity
																			: 0),
																0,
															)),
													0,
												)
												?.toFixed(1)}
										</b>
									</TableCell>

									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
										}}>
										<b>
											{statistics
												?.reduce(
													(sum, item) =>
														(sum +=
															+item.transferOrder -
															+item.receiveOrder +
															item?.transferOrderDetail?.reduce(
																(sum, it) =>
																	(sum +=
																		it?.status === 'confirm-delete-produce' ||
																		it?.status === 'confirm-delete-ship' ||
																		it?.status === 'confirm-delete-success'
																			? +it?.quantity
																			: 0),
																0,
															) +
															item?.receiveOrderDetail?.reduce(
																(sum, it) =>
																	(sum +=
																		it?.status === 'confirm-delete-produce' ||
																		it?.status === 'confirm-delete-ship' ||
																		it?.status === 'confirm-delete-success'
																			? +it?.quantity
																			: 0),
																0,
															)),
													0,
												)
												?.toFixed(1)}
										</b>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</Box>
				)}
			</Box>
			<Box
				sx={{
					background: '#fff',
					color: '#000',
					borderRadius: '5px',
				}}>
				{currentSelect === 'transfer' && (
					<Typography
						sx={{
							fontWeight: 'bold',
							textAlign: 'center',
							fontSize: '18px',
							letterSpacing: '2px',
							mb: 1,
						}}>
						ĐƠN CHUYỂN ĐẾN {statistics[currentIndex]?.store.toUpperCase()}
					</Typography>
				)}
				{currentSelect === 'transfer' && statistics[currentIndex]?.transferOrderDetail?.length === 0 && (
					<Typography sx={{ fontStyle: 'italic', textAlign: 'center', pb: 1 }}>(Chưa có dữ liệu)</Typography>
				)}
				{currentSelect === 'transfer' &&
					statistics[currentIndex]?.transferOrderDetail?.map((order, index) => {
						return (
							order?.shiftCode && (
								<Card
									key={index}
									sx={{
										marginBottom: '10px',
										padding: '10px',
										textAlign: 'left',
									}}>
									<Box display='flex' alignItems='center'>
										<Typography
											sx={{
												display: 'inline-flex',
												padding: '0 10px',
												background: mainGreen,
												color: '#fff',
												borderRadius: '10px',
												mb: 1,
											}}>
											{' '}
											{order?.shiftCode}
											{order?.shiftIndex} - {order?.shiftDay}
										</Typography>
										{(order?.status === 'confirm-delete-produce' ||
											order?.status === 'confirm-delete-ship' ||
											order?.status === 'confirm-delete-success') && (
											<Typography
												sx={{
													display: 'inline-flex',
													padding: '0 10px',
													background: mainRed,
													color: '#fff',
													borderRadius: '10px',
													mb: 1,
													ml: 1,
												}}>
												Đã hủy
											</Typography>
										)}
									</Box>

									<Box sx={{ p: 0 }}>
										<Typography sx={{ fontSize: '15px' }}>
											<b>Khối lượng: </b>
											{order?.quantity}kg - <b>Tiền: </b>
											{order?.totalPrice.toLocaleString()}đ{' '}
											{order?.payment === 'transfer' && (
												<span style={{ color: mainGreen, fontWeight: 'bold' }}>(Chuyển khoản)</span>
											)}{' '}
											{order?.app && <span style={{ color: mainRed, fontWeight: 'bold' }}>(App)</span>}
										</Typography>
										<Typography sx={{ fontSize: '15px' }}>
											<b>Thông tin: </b>
											{order?.detail && (
												<span
													dangerouslySetInnerHTML={{
														__html: transformDetailWithPhone(order?.detail),
													}}></span>
											)}
										</Typography>
										{order?.tag?.map((tag, index) => {
											return (
												<Typography
													sx={{
														display: 'inline-block',
														fontSize: '12px',
														padding: '2px 10px',
														borderRadius: '10px',
														color: '#fff',
														background: `${tag.color}`,
														mr: 1,
													}}
													key={index}>
													{tag.name}
												</Typography>
											)
										})}
										<Box>
											{order?.photoAttach.map((photo, index) => {
												return (
													<Fragment key={index}>
														<img
															loading='lazy'
															src={photo}
															onClick={(e) => {
																e.stopPropagation()
																openImageViewer(photo)
															}}
															width='30'
															height='30'
															style={{
																marginRight: '8px',
																marginTop: '5px',
																cursor: 'pointer',
															}}
															alt=''
														/>
													</Fragment>
												)
											})}
										</Box>
									</Box>
								</Card>
							)
						)
					})}
				{currentSelect === 'receive' && (
					<Typography
						sx={{
							fontWeight: 'bold',
							textAlign: 'center',
							fontSize: '18px',
							letterSpacing: '2px',
							mb: 1,
						}}>
						ĐƠN NHẬN TỪ {statistics[currentIndex]?.store.toUpperCase()}
					</Typography>
				)}
				{currentSelect === 'receive' && statistics[currentIndex]?.receiveOrderDetail?.length === 0 && (
					<Typography sx={{ fontStyle: 'italic', textAlign: 'center', pb: 1 }}>(Chưa có dữ liệu)</Typography>
				)}
				{currentSelect === 'receive' &&
					statistics[currentIndex]?.receiveOrderDetail?.map((order, index) => {
						return (
							order?.shiftCode && (
								<Card
									key={index}
									sx={{
										marginBottom: '10px',
										padding: '10px',
										textAlign: 'left',
									}}>
									<Box display='flex' alignItems='center'>
										<Typography
											sx={{
												display: 'inline-flex',
												padding: '0 10px',
												background: mainGreen,
												color: '#fff',
												borderRadius: '10px',
												mb: 1,
											}}>
											{' '}
											{order?.shiftCode}
											{order?.shiftIndex} - {order?.shiftDay}
										</Typography>
										{(order?.status === 'confirm-delete-produce' ||
											order?.status === 'confirm-delete-ship' ||
											order?.status === 'confirm-delete-success') && (
											<Typography
												sx={{
													display: 'inline-flex',
													padding: '0 10px',
													background: mainRed,
													color: '#fff',
													borderRadius: '10px',
													mb: 1,
													ml: 1,
												}}>
												Đã hủy
											</Typography>
										)}
									</Box>

									<Box sx={{ p: 0 }}>
										<Typography sx={{ fontSize: '15px' }}>
											<b>Khối lượng: </b>
											{order?.quantity}kg - <b>Tiền: </b>
											{order?.totalPrice.toLocaleString()}đ{' '}
											{order?.payment === 'transfer' && (
												<span style={{ color: mainGreen, fontWeight: 'bold' }}>(Chuyển khoản)</span>
											)}{' '}
											{order?.app && <span style={{ color: mainRed, fontWeight: 'bold' }}>(App)</span>}
										</Typography>
										<Typography sx={{ fontSize: '15px' }}>
											<b>Thông tin: </b>
											{order?.detail && (
												<span
													dangerouslySetInnerHTML={{
														__html: transformDetailWithPhone(order?.detail),
													}}></span>
											)}
										</Typography>
										{order?.tag?.map((tag, index) => {
											return (
												<Typography
													sx={{
														display: 'inline-block',
														fontSize: '12px',
														padding: '2px 10px',
														borderRadius: '10px',
														color: '#fff',
														background: `${tag.color}`,
														mr: 1,
													}}
													key={index}>
													{tag.name}
												</Typography>
											)
										})}
										<Box>
											{order?.photoAttach.map((photo, index) => {
												return (
													<Fragment key={index}>
														<img
															loading='lazy'
															src={photo}
															onClick={(e) => {
																e.stopPropagation()
																openImageViewer(photo)
															}}
															width='30'
															height='30'
															style={{
																marginRight: '8px',
																marginTop: '5px',
																cursor: 'pointer',
															}}
															alt=''
														/>
													</Fragment>
												)
											})}
										</Box>
									</Box>
								</Card>
							)
						)
					})}
			</Box>
		</UserLayout>
	)
}
